import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import ComicCard from "@/components/base/ComicCard";
import { comics, categories } from "@/mocks/comics";

type SortKey = "hot" | "update" | "rating";

const statuses = ["全部", "连载中", "已完结"];

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const type = searchParams.get("type") || "全部";
  const keyword = searchParams.get("q") || "";
  const [status, setStatus] = useState("全部");
  const [sort, setSort] = useState<SortKey>("hot");

  const setType = (t: string) => {
    const next = new URLSearchParams(searchParams);
    if (t === "全部") next.delete("type");
    else next.set("type", t);
    setSearchParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    let list = [...comics];
    if (type !== "全部") list = list.filter((c) => c.category === type);
    if (status !== "全部") list = list.filter((c) => c.status === status);
    if (keyword) list = list.filter((c) => c.title.includes(keyword) || c.tags.some((t) => t.includes(keyword)));
    if (sort === "hot") list.sort((a, b) => b.follows - a.follows);
    else if (sort === "rating") list.sort((a, b) => b.rating - a.rating);
    return list;
  }, [type, status, keyword, sort]);

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        {/* Breadcrumb */}
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1 text-sm text-foreground-500">
          <a href="/" className="hover:text-primary-600">首页</a>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700">分类浏览</span>
        </nav>

        <header className="mt-4">
          <h1 className="font-heading text-3xl text-foreground-950 md:text-4xl">漫画分类</h1>
          <p className="mt-2 text-sm text-foreground-600">
            {keyword ? `搜索“${keyword}”` : type === "全部" ? "全部分类" : type}
            ,共 {filtered.length} 部作品
          </p>
        </header>

        {/* Category tabs */}
        <div className="mt-6 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setType(c)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                type === c
                  ? "bg-primary-600 text-background-50"
                  : "border border-background-200 bg-background-50 text-foreground-700 hover:border-primary-400"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Status + sort toolbar */}
        <div className="mt-4 flex flex-col gap-3 rounded-xl border border-background-200/80 bg-background-50 p-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground-500">状态</span>
            {statuses.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={`rounded-full px-3 py-1 text-xs transition-colors ${
                  status === s
                    ? "bg-secondary-500 text-background-50"
                    : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground-500">排序</span>
            {(
              [
                { k: "hot", label: "人气" },
                { k: "update", label: "更新" },
                { k: "rating", label: "评分" },
              ] as { k: SortKey; label: string }[]
            ).map((s) => (
              <button
                key={s.k}
                type="button"
                onClick={() => setSort(s.k)}
                className={`rounded-full px-3 py-1 text-xs transition-colors ${
                  sort === s.k
                    ? "bg-primary-600 text-background-50"
                    : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
            {filtered.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center py-16 text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
            <p className="mt-4 text-sm text-foreground-600">没有找到符合条件的漫画</p>
            <button
              type="button"
              onClick={() => {
                setStatus("全部");
                setType("全部");
                setSearchParams({}, { replace: true });
              }}
              className="mt-4 whitespace-nowrap rounded-full bg-primary-600 px-6 py-2.5 text-sm text-background-50 hover:bg-primary-700"
            >
              重置筛选
            </button>
          </div>
        )}

        {/* ItemList structured data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              name: type === "全部" ? "策驰漫画网 - 全部分类" : `策驰漫画网 - ${type}`,
              description: `策驰漫画网${type === "全部" ? "" : type}分类漫画作品,支持翻页阅读、目录跳转、收藏追更与阅读记录同步。`,
              mainEntity: {
                "@type": "ItemList",
                itemListElement: filtered.slice(0, 20).map((c, i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  name: c.title,
                  url: `https://faequest.github.io/comic/${c.id}`,
                })),
              },
            }),
          }}
        />
      </div>
    </Layout>
  );
}